import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, KeyRound, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/f617a5a9-dda7-40dc-bbb9-de762658adaf/images/5d32d876981551b4c98ceec76bfdad1e26bc99093c09f2a5f2242b603b114d79.png";

export default function VerifyEmailPage({ setUser }) {
  const location = useLocation();
  const emailFromState = location.state?.email || '';
  
  const [email, setEmail] = useState(emailFromState);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      setUser(response.data.user);
      toast.success('Email verified successfully!');
      
      if (response.data.user.role === 'admin') navigate('/admin', { replace: true });
      else if (response.data.user.role === 'scorer') navigate('/scorer', { replace: true });
      else navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setResending(true);

    try {
      const response = await axios.post(`${API}/auth/resend-code`, { email });
      toast.success('Verification code sent!');
      
      // For demo, show the code
      if (response.data.verification_code) {
        toast.info(`New code: ${response.data.verification_code}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex flex-col">
      {/* Header */}
      <div className="p-4">
        <Link to="/login" className="inline-flex items-center gap-2 text-white/80 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
          Back to Login
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-3xl shadow-2xl border-0">
          <CardContent className="p-8">
            {/* Icon */}
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="CricORama" className="w-16 h-16 object-contain mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 font-display">Verify Your Email</h1>
              <p className="text-slate-500 mt-1">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-5">
              {!emailFromState && (
                <div className="space-y-2">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 h-12 rounded-xl"
                      required
                      data-testid="verify-email-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-12 h-12 rounded-xl text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    required
                    data-testid="verify-code-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-lg shadow-blue-600/30"
                disabled={loading || code.length !== 6}
                data-testid="verify-submit"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={handleResendCode}
                disabled={resending}
                className="text-blue-600 font-medium hover:underline inline-flex items-center gap-2"
                data-testid="resend-code-btn"
              >
                <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Sending...' : 'Resend Code'}
              </button>
            </div>

            {/* Demo Note */}
            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Demo Mode:</strong> The verification code is displayed in a toast notification. In production, it would be sent via email.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
